import React from 'react';
import SEO from '../Components/SEO';
import { Button } from '../Components/ui/button';
import { Badge } from '../Components/ui/badge';
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../Components/ui/card';

const FLUIDIFY_URL = 'https://www.fluidify.org';
const INFOCOM_STUDIOS_URL = 'https://infocom-studios.com';

const IMG_STUDIOS =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=960&q=80';

const FLUIDIFY_VIDEO_SRC = `${process.env.PUBLIC_URL}/6092366_Buildings%20Infographics%20Grey%203d_By_Finn_Moeller_HD.mp4`;

const FLUIDIFY_DESCRIPTION =
  'Fluidify maps planned and under-construction infrastructure projects worldwide and detects emerging industrial clusters, corridors, and export nodes.';

const LINKEDIN_ALEX = 'https://www.linkedin.com/in/alex-nesovic';

function LinkedInMark({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function HomeFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="pointer-events-none fixed bottom-0 left-0 z-40 px-6 pb-6 pt-4 md:left-8 md:pb-8 md:pt-6">
      <div className="pointer-events-auto flex flex-col items-start gap-2.5 text-left">
        <a
          href={LINKEDIN_ALEX}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2.5 no-underline outline-none transition-colors hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-black text-white shadow-sm ring-1 ring-white/10 transition-transform group-hover:scale-[1.02]"
            aria-hidden="true"
          >
            <LinkedInMark className="h-4 w-4" />
          </span>
          <span className="text-[13px] font-medium tracking-tight text-foreground/95 underline decoration-transparent underline-offset-4 transition-colors group-hover:text-foreground group-hover:decoration-foreground/30">
            Alex Nesovic
          </span>
        </a>
        <p className="m-0 text-[11px] font-medium tabular-nums tracking-wide text-muted-foreground">
          © {year} Infocom
        </p>
        <p className="m-0 max-w-[14rem] text-[10px] font-normal uppercase leading-relaxed tracking-[0.18em] text-muted-foreground/75">
          All rights reserved
        </p>
      </div>
    </footer>
  );
}

function DestinationCard({ href, imageSrc, videoSrc, imageAlt, badge, title, description, cta }) {
  return (
    <Card className="relative flex h-full min-h-0 w-full max-w-sm flex-col overflow-hidden border-0 pt-0 shadow-xl ring-1 ring-white/10 md:max-w-none">
      <div className="relative aspect-video w-full shrink-0">
        <div className="absolute inset-0 z-30 aspect-video bg-black/35" aria-hidden="true" />
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAlt}
            className="relative z-20 aspect-video h-full w-full object-cover brightness-60 grayscale dark:brightness-40"
          />
        ) : videoSrc ? (
          <video
            className="relative z-20 aspect-video h-full w-full object-cover brightness-60 grayscale dark:brightness-40"
            autoPlay
            loop
            muted
            playsInline
            aria-label={imageAlt}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        ) : (
          <div
            role="img"
            aria-label={imageAlt}
            className="relative z-20 aspect-video h-full w-full bg-gradient-to-br from-indigo-950 via-violet-900 to-slate-950 brightness-[0.55] grayscale dark:brightness-[0.35]"
          />
        )}
      </div>
      <CardHeader className="relative z-40 flex flex-1 flex-col gap-3">
        <CardAction>
          <Badge variant="secondary">{badge}</Badge>
        </CardAction>
        <CardTitle className="text-balance text-card-foreground">{title}</CardTitle>
        <CardDescription className="text-pretty text-justify">{description}</CardDescription>
      </CardHeader>
      <CardFooter className="mt-auto shrink-0 px-5 pb-5 pt-0 sm:px-6 sm:pb-6">
        <Button
          size="sm"
          variant="default"
          className="w-full !h-8 rounded-md px-3 text-xs font-medium shadow-sm"
          asChild
        >
          <a href={href} rel="noopener noreferrer" className="!no-underline hover:!no-underline">
            {cta}
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
}

function Home() {
  return (
    <>
      <SEO
        title="Infocom — Fluidify & Infocom Studios"
        description="Choose Fluidify or Infocom Studios."
        keywords="Fluidify, Infocom Studios, Infocom, infocom-studios.com"
        canonical={FLUIDIFY_URL}
      />
      <main className="dark relative box-border flex min-h-screen flex-col bg-background px-4 pb-28 pt-16 font-syne antialiased md:pb-32 md:pt-20">
        <a
          href="/"
          className="anim-logo fixed left-6 top-4 z-50 text-xl font-medium tracking-tight text-foreground no-underline outline-none ring-offset-background transition-opacity hover:opacity-90 hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:left-8 md:text-2xl"
          aria-label="Infocom home"
        >
          Infocom
        </a>
        <div className="flex min-h-0 flex-1 flex-col justify-center">
          <div className="mx-auto grid w-full max-w-sm grid-cols-1 justify-items-center gap-3 md:max-w-[49rem] md:grid-cols-2 md:items-stretch md:justify-items-stretch md:gap-4">
            <div className="anim-card-1 flex h-full w-full justify-items-stretch">
              <DestinationCard
                href={FLUIDIFY_URL}
                videoSrc={FLUIDIFY_VIDEO_SRC}
                imageAlt="Fluidify preview video"
                badge="AI systems"
                title="Fluidify"
                description={FLUIDIFY_DESCRIPTION}
                cta="Open Fluidify"
              />
            </div>
            <div className="anim-card-2 flex h-full w-full justify-items-stretch">
              <DestinationCard
                href={INFOCOM_STUDIOS_URL}
                imageSrc={IMG_STUDIOS}
                imageAlt="Studio workspace"
                badge="Design"
                title="Infocom Studios"
                description="We help all businesses elevate their presence online, with contemporary digital experiences that look the part and feel effortless."
                cta="Open Infocom Studios"
              />
            </div>
          </div>
        </div>
        <HomeFooter />
      </main>
    </>
  );
}

export default Home;
